import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import DcStock from "../../DcStock"
import { neumorph } from "../neumorph"
import { formatCurrency, formatNum } from "../KpiHeader"

const DcStockCard = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/dc-stock`,
          { supplier, retailer, userid: localStorage.getItem("userid") },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setItems(res.data.data?.summary || [])
        else setItems([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("DcStockCard fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer])

  const totalValue = items.reduce((s, r) => s + (parseFloat(r.total_soh_value) || 0), 0)
  const totalQty = items.reduce((s, r) => s + (parseInt(r.total_soh_qty) || 0), 0)
  const maxValue = items.length ? Math.max(...items.map((r) => parseFloat(r.total_soh_value) || 0)) : 0

  return (
    <>
      <BentoCard
        title="🏭 DC Stock by Warehouse"
        subtitle="Distribution centre breakdown"
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        {items.length === 0 ? (
          <Flex h="100%" align="center" justify="center" direction="column" textAlign="center">
            <Text fontSize="xs" color={neumorph.textMuted}>
              No DC stock data
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" h="100%" gap={2}>
            <Flex gap={3} mb={1}>
              <Box>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  Total
                </Text>
                <Text fontSize="md" fontWeight="700" color={neumorph.accentTealDark}>
                  {formatCurrency(totalValue)}
                </Text>
                <Text fontSize="2xs" color={neumorph.textMuted}>
                  {formatNum(totalQty)} units across {items.length} DC{items.length !== 1 ? "s" : ""}
                </Text>
              </Box>
            </Flex>
            <Box flex="1" overflowY="auto" maxH="160px">
              {items.map((dc) => {
                const value = parseFloat(dc.total_soh_value) || 0
                const pct = maxValue > 0 ? (value / maxValue) * 100 : 0
                return (
                  <Box key={dc.site_code} mb={2}>
                    <Flex justify="space-between" mb={0.5}>
                      <Text fontSize="2xs" color={neumorph.textPrimary} fontWeight="600" isTruncated maxW="60%">
                        {dc.site_description || dc.site_code}
                      </Text>
                      <Text fontSize="2xs" fontWeight="700" color={neumorph.accentTealDark}>
                        {formatCurrency(value)}
                      </Text>
                    </Flex>
                    <Box bg="rgba(174,184,200,0.2)" borderRadius="full" h="6px" overflow="hidden">
                      <Box
                        bg={`linear-gradient(90deg, ${neumorph.accentTeal}, ${neumorph.accentTealDark})`}
                        h="100%"
                        w={`${pct}%`}
                        borderRadius="full"
                        transition="width 0.4s ease"
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Flex>
        )}
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="DC Stock by Warehouse">
        <DcStock supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default DcStockCard
